/**
 * Scooter delivery + collection fees (one price per zone, set once by the
 * provider for all their scooters — backend/services/delivery_rates.py).
 * The server prices and freezes them; these helpers only read and display.
 */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Total for the traveller: rental + delivery and collection. */
export const grandTotal = (rental, fee) => Math.round((num(rental) + num(fee)) * 100) / 100;

/**
 * Amounts of a booking. `priced`: a delivery priced by the provider's fees
 * (rental, delivery and total shown apart). Older bookings and deliveries
 * arranged with the provider: the booking amount as before.
 */
export const bookingAmounts = (b) => {
  const rental = num(b?.price_original ?? b?.price);
  const priced = Boolean(b?.delivery_requested) && b?.delivery_fee != null && b?.total_price != null;
  const fee = priced ? num(b.delivery_fee) : 0;
  return {
    rental,
    priced,
    deliveryFee: priced ? fee : null,
    zone: priced ? (b.delivery_details?.zone_name || '') : '',
    total: priced ? grandTotal(rental, fee) : rental,
  };
};

/** Rows of the provider's settings: every zone, with its saved price and state. */
export const ratesToRows = (zones, rates) => {
  const saved = Object.fromEntries((rates || []).map((r) => [r.zone_id, r]));
  return (zones || []).map((z) => ({
    zone_id: z.zone_id,
    zone_name: z.name || saved[z.zone_id]?.zone_name || z.zone_id,
    fee: saved[z.zone_id] ? String(saved[z.zone_id].fee) : '',
    active: Boolean(saved[z.zone_id]?.active),
  }));
};

/**
 * What is sent to the server: the zones with a price (active or not). An
 * active zone without a valid price is an error (`missing` lists them).
 */
export const rowsToPayload = (rows) => {
  const rates = [];
  const missing = [];
  for (const r of rows || []) {
    const text = String(r.fee ?? '').trim().replace(',', '.');
    const fee = text === '' ? null : Number(text);
    const valid = fee != null && Number.isFinite(fee) && fee >= 0 && fee <= 100000;
    if (r.active && !valid) missing.push(r.zone_id);
    if (valid) rates.push({ zone_id: r.zone_id, fee, active: Boolean(r.active) });
  }
  return { ok: missing.length === 0, rates, missing };
};
